import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  CircularProgress,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { adminOrderService, orderService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import { VIEWER_TIMEZONE } from '../hooks/useDashboard'
import { buildDashboardReturnSearch } from '../utils/undeliveredQuantityFilters'
import { formatDate, formatDateTime } from '../utils/dateFormat'
import { formatOrderAmount } from '../utils/ordersListUtils'
import {
  hasAdminRole,
  ADMIN_ROLE_CODES,
  canExportData,
} from '../utils/adminPermissions'
import useAuthStore from '../store/authStore'
import PaymentProofVerifyDialog from '../components/orders/PaymentProofVerifyDialog'
import AdminPaymentProofPreviewDialog from '../components/orders/AdminPaymentProofPreviewDialog'
import TableEmptyState from '../components/shared/TableEmptyState'
import TableLoadingRow from '../components/shared/TableLoadingRow'
import DashboardSearchableSelect from '../components/Dashboard/DashboardSearchableSelect'
import { useDashboardFilterOptions } from '../hooks/useDashboardFilterOptions'
import { joinDashboardIds } from '../hooks/useDashboard'
import { downloadBlobResponse } from '../components/price-concerns/priceConcernUtils'

const PAYMENT_PROOF_REJECT_REASON_MAX = 500

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '3d', label: 'Last 3 Days' },
  { value: '7d', label: 'Last 7 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'custom', label: 'Custom Range' },
]

const headerCellSx = { fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }

const paginationSx = {
  width: '100%',
  overflowX: 'auto',
  '& .MuiToolbar-root': {
    flexWrap: 'wrap',
    justifyContent: { xs: 'center', sm: 'flex-end' },
    minHeight: { xs: 48, sm: 52 },
    px: { xs: 0.5, sm: 2 },
    gap: 0.5,
  },
}

const cellDash = (value) => {
  if (value == null || value === '') return '—'
  return value
}

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  {
    value: 'awaiting_verification',
    label: 'Awaiting verification',
  },
  { value: 'new', label: 'New — awaiting verification' },
  { value: 'edited', label: 'Edited — awaiting verification' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'verified', label: 'Verified' },
]

function paymentProofStatusCell(row) {
  const statusUpper = String(row.status || '').toUpperCase()
  const editedAfterRejection = Boolean(row.edited_after_rejection)
  if (statusUpper === 'VERIFIED') {
    return (
      <Chip label="Verified" color="success" size="small" variant="outlined" />
    )
  }
  if (statusUpper === 'REJECTED') {
    return (
      <Chip label="Rejected" color="error" size="small" variant="outlined" />
    )
  }
  const pendingLabel = editedAfterRejection
    ? 'Edited — awaiting verification'
    : 'New — awaiting verification'
  return (
    <Chip label={pendingLabel} color="warning" size="small" variant="outlined" />
  )
}

const isLikelyImageUrl = (url) => /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url)

const PaymentProcessingListPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const [searchParams, setSearchParams] = useSearchParams()

  const page = Math.max(0, parseInt(searchParams.get('page') || '0', 10) || 0)
  const rowsPerPage = ROWS_PER_PAGE_OPTIONS.includes(
    parseInt(searchParams.get('limit') || '25', 10),
  )
    ? parseInt(searchParams.get('limit') || '25', 10)
    : 25
  const search = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || ''
  const shwapnoAccountFilter = searchParams.get('shwapno_account') || ''
  const datePreset = searchParams.get('datePreset') || '7d'
  const dateFrom = searchParams.get('dateFrom') || ''
  const dateTo = searchParams.get('dateTo') || ''
  const locationIds = searchParams.get('locationIds') || ''
  const categoryIds = searchParams.get('categoryIds') || ''
  const organizationIds = searchParams.get('organizationIds') || ''

  const customReady =
    datePreset !== 'custom' || (Boolean(dateFrom) && Boolean(dateTo) && dateFrom <= dateTo)
  const scopeActive = Boolean(locationIds || categoryIds || organizationIds)

  const dashboardFilters = useMemo(
    () => ({
      locationIds,
      categoryIds,
      organizationIds,
    }),
    [locationIds, categoryIds, organizationIds],
  )
  const { partners, searchPartners, partnerLoading } =
    useDashboardFilterOptions(null, dashboardFilters)

  const [searchInput, setSearchInput] = useState(search)
  const [rows, setRows] = useState([])
  const [shwapnoAccountOptions, setShwapnoAccountOptions] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [slipGallery, setSlipGallery] = useState(null)
  const [listRefreshToken, setListRefreshToken] = useState(0)
  const [actionFeedback, setActionFeedback] = useState(null)
  const [paymentProofVerifyTarget, setPaymentProofVerifyTarget] = useState(null)
  const [paymentProofRequestActionId, setPaymentProofRequestActionId] = useState(null)
  const [paymentProofRejectTarget, setPaymentProofRejectTarget] = useState(null)
  const [paymentProofRejectReason, setPaymentProofRejectReason] = useState('')
  const [paymentProofRejectError, setPaymentProofRejectError] = useState(null)
  const [paymentProofRejectSubmitting, setPaymentProofRejectSubmitting] = useState(false)
  const [paymentProofPreviewTarget, setPaymentProofPreviewTarget] = useState(null)
  const [paymentProofProofActionSubmitting, setPaymentProofProofActionSubmitting] =
    useState(false)

  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const showDownloadExcel = canExportData(adminPermissions)
  const canVerifyPaymentProof = hasAdminRole(
    adminRoleCodes,
    ADMIN_ROLE_CODES.PAYMENT_VERIFICATION,
  )
  const tableColSpan = canVerifyPaymentProof ? 20 : 19

  useEffect(() => {
    setSearchInput(search)
  }, [search])

  useEffect(() => {
    const requestId = paymentProofPreviewTarget?.request_id
    if (!requestId) return
    const proofId = paymentProofPreviewTarget?.proof_id
    const updated = rows.find((r) =>
      proofId != null
        ? r.request_id === requestId && r.proof_id === proofId
        : r.request_id === requestId,
    )
    if (updated) {
      setPaymentProofPreviewTarget(updated)
    }
  }, [rows, paymentProofPreviewTarget?.request_id, paymentProofPreviewTarget?.proof_id])

  const replaceParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams)
      Object.entries(updates).forEach(([key, value]) => {
        if (value == null || value === '') next.delete(key)
        else next.set(key, String(value))
      })
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput === search) return
      replaceParams({ search: searchInput.trim(), page: 0 })
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, search, replaceParams])

  const queryKey = useMemo(
    () =>
      [
        search,
        statusFilter,
        shwapnoAccountFilter,
        locationIds,
        categoryIds,
        organizationIds,
        datePreset,
        dateFrom,
        dateTo,
        page,
        rowsPerPage,
        listRefreshToken,
      ].join('|'),
    [
      search,
      statusFilter,
      shwapnoAccountFilter,
      locationIds,
      categoryIds,
      organizationIds,
      datePreset,
      dateFrom,
      dateTo,
      page,
      rowsPerPage,
      listRefreshToken,
    ],
  )

  useEffect(() => {
    if (!customReady) {
      setLoading(false)
      setRows([])
      setTotal(0)
      return undefined
    }
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    const listParams = {
      page: page + 1,
      limit: rowsPerPage,
      viewerTimezone: VIEWER_TIMEZONE,
      datePreset,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(shwapnoAccountFilter
        ? { shwapno_payment_settings_id: shwapnoAccountFilter }
        : {}),
      ...(locationIds ? { locationIds } : {}),
      ...(categoryIds ? { categoryIds } : {}),
      ...(organizationIds ? { organizationIds } : {}),
    }
    if (datePreset === 'custom') {
      listParams.dateFrom = dateFrom
      listParams.dateTo = dateTo
    }
    orderService
      .getPaymentProcessingList(listParams)
      .then((response) => {
        if (controller.signal.aborted) return
        setRows(response.items || [])
        setShwapnoAccountOptions(response.shwapno_account_options || [])
        setTotal(response.pagination?.total ?? 0)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setRows([])
        setTotal(0)
        setError(getApiErrorMessage(err, 'Failed to load payment processing list'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [
    queryKey,
    customReady,
    search,
    statusFilter,
    shwapnoAccountFilter,
    locationIds,
    categoryIds,
    organizationIds,
    datePreset,
    dateFrom,
    dateTo,
    page,
    rowsPerPage,
  ])

  const buildFilterParams = useCallback(() => {
    const params = {
      viewerTimezone: VIEWER_TIMEZONE,
      datePreset,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(shwapnoAccountFilter
        ? { shwapno_payment_settings_id: shwapnoAccountFilter }
        : {}),
      ...(locationIds ? { locationIds } : {}),
      ...(categoryIds ? { categoryIds } : {}),
      ...(organizationIds ? { organizationIds } : {}),
    }
    if (datePreset === 'custom') {
      params.dateFrom = dateFrom
      params.dateTo = dateTo
    }
    return params
  }, [
    search,
    statusFilter,
    shwapnoAccountFilter,
    locationIds,
    categoryIds,
    organizationIds,
    datePreset,
    dateFrom,
    dateTo,
  ])

  const handleExportExcel = async () => {
    if (exporting || !customReady || total === 0) return
    try {
      setExporting(true)
      const response = await orderService.exportPaymentProcessingExcel(
        buildFilterParams(),
      )
      downloadBlobResponse(response, 'payment-processing.xlsx')
      setActionFeedback({
        severity: 'success',
        message: 'Excel download is ready.',
      })
    } catch (err) {
      setActionFeedback({
        severity: 'error',
        message: getApiErrorMessage(err, 'Could not export payment processing list.'),
      })
    } finally {
      setExporting(false)
    }
  }

  const exportDisabled = exporting || loading || !customReady || total === 0

  const handleBack = () => {
    if (location.state?.dashboardSearch != null) {
      const dashSearch = location.state.dashboardSearch
      navigate(dashSearch ? `/dashboard?${dashSearch}` : '/dashboard')
      return
    }
    const dashSearch = buildDashboardReturnSearch({
      locationIds,
      categoryIds,
      organizationIds,
      datePreset,
      dateFrom,
      dateTo,
    })
    navigate(dashSearch ? `/dashboard?${dashSearch}` : '/dashboard')
  }

  const handleOrderClick = (orderId) => {
    navigate(`/orders/${orderId}`, {
      state: {
        ordersListPath: `${location.pathname}${location.search}`,
      },
    })
  }

  const closePaymentProofRejectDialog = () => {
    if (paymentProofRejectSubmitting) return
    setPaymentProofRejectTarget(null)
    setPaymentProofRejectReason('')
    setPaymentProofRejectError(null)
  }

  const rowActionKey = (row) =>
    row?.proof_id != null
      ? `${row.request_id}-${row.proof_id}`
      : String(row?.request_id ?? '')

  const handleVerifyRow = (row) => {
    // Match Order Detail: request-level verify always requires SAP doc no.
    // Per-proof verify (optional SAP until last pending) stays in the preview dialog.
    if (!row?.request_id) return
    const requested =
      row.request_requested_amount != null
        ? row.request_requested_amount
        : row.amount
    setPaymentProofVerifyTarget({
      id: row.request_id,
      order_id: row.order_id,
      requested_amount: requested,
    })
  }

  const handleConfirmVerifyPaymentProofRequest = async (payload) => {
    if (!paymentProofVerifyTarget?.id) return
    try {
      setPaymentProofRequestActionId(
        String(paymentProofVerifyTarget.id),
      )
      setActionFeedback(null)
      if (paymentProofVerifyTarget.order_id) {
        await adminOrderService.verifyPaymentProofRequest(
          paymentProofVerifyTarget.order_id,
          paymentProofVerifyTarget.id,
          payload,
        )
      } else {
        await adminOrderService.verifyPaymentProofRequestById(
          paymentProofVerifyTarget.id,
          payload,
        )
      }
      setPaymentProofVerifyTarget(null)
      setActionFeedback({
        severity: 'success',
        message: 'Payment proof verified.',
      })
      setListRefreshToken((t) => t + 1)
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to verify payment proof')
      throw new Error(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofRequestActionId(null)
    }
  }

  const handleVerifyProofInPreview = async ({ requestId, proofId, payload }) => {
    try {
      setPaymentProofProofActionSubmitting(true)
      setActionFeedback(null)
      await adminOrderService.verifyPaymentProofById(requestId, proofId, payload)
      setActionFeedback({
        severity: 'success',
        message: 'Payment proof file verified.',
      })
      setListRefreshToken((t) => t + 1)
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to verify payment proof')
      throw new Error(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofProofActionSubmitting(false)
    }
  }

  const handleRejectProofInPreview = async ({ requestId, proofId, reason }) => {
    try {
      setPaymentProofProofActionSubmitting(true)
      setActionFeedback(null)
      await adminOrderService.rejectPaymentProofById(requestId, proofId, reason)
      setActionFeedback({
        severity: 'success',
        message: 'Payment proof file rejected.',
      })
      setListRefreshToken((t) => t + 1)
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to reject payment proof')
      throw new Error(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofProofActionSubmitting(false)
    }
  }

  const handleConfirmRejectPaymentProofRequest = async () => {
    if (!paymentProofRejectTarget?.id) return
    const reason = paymentProofRejectReason.trim()
    if (!reason) {
      setPaymentProofRejectError('Rejection reason is required.')
      return
    }
    try {
      setPaymentProofRejectSubmitting(true)
      setPaymentProofRejectError(null)
      if (paymentProofRejectTarget.proofId) {
        await adminOrderService.rejectPaymentProofById(
          paymentProofRejectTarget.id,
          paymentProofRejectTarget.proofId,
          reason,
        )
      } else if (paymentProofRejectTarget.order_id) {
        await adminOrderService.rejectPaymentProofRequest(
          paymentProofRejectTarget.order_id,
          paymentProofRejectTarget.id,
          reason,
        )
      } else {
        await adminOrderService.rejectPaymentProofRequestById(
          paymentProofRejectTarget.id,
          reason,
        )
      }
      closePaymentProofRejectDialog()
      setActionFeedback({
        severity: 'success',
        message: 'Payment proof request rejected.',
      })
      setListRefreshToken((t) => t + 1)
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to reject payment proof request')
      setPaymentProofRejectError(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofRejectSubmitting(false)
    }
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          Dashboard
        </Button>
      </Stack>

      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {loading ? (
            <CircularProgress size={22} sx={{ color: '#e90000' }} />
          ) : null}
          <Typography variant="h5" fontWeight={700}>
            Payment Processing
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {statusFilter === 'rejected'
            ? 'Rejected payment proofs and standalone shop deposits — one row per proof file, with the same deposit or order ID when multiple files were uploaded.'
            : statusFilter
              ? 'Payments awaiting verification — order-linked proofs and standalone shop deposits from all partner shops.'
              : 'All payment proofs in the finance queue — one row per proof file (deposit/order IDs repeat when a deposit has multiple files).'}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {actionFeedback && (
        <Alert
          severity={actionFeedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setActionFeedback(null)}
        >
          {actionFeedback.message}
        </Alert>
      )}

      {scopeActive && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                replaceParams({
                  locationIds: '',
                  categoryIds: '',
                  organizationIds: '',
                  page: 0,
                })
              }
            >
              Clear scope
            </Button>
          }
        >
          Limited to the dashboard location, category, and partner shop filters.
        </Alert>
      )}

      <Card sx={{ mb: 2, p: 2 }}>
        <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            label="Search shop, deposit ID (DEP-…), or order ID"
            size="small"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            fullWidth
            sx={{ maxWidth: { md: 480 }, flex: { md: '1 1 320px' } }}
          />
          <DashboardSearchableSelect
            label="Partner shop"
            options={partners}
            value={organizationIds}
            multiple
            onChange={(opts) =>
              replaceParams({
                organizationIds: joinDashboardIds(opts),
                page: 0,
              })
            }
            onSearch={searchPartners}
            loading={partnerLoading}
            sx={{ minWidth: { xs: '100%', md: 280 }, flex: { md: '1 1 260px' } }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
            <InputLabel id="payment-processing-status-filter-label" shrink>
              Status
            </InputLabel>
            <Select
              labelId="payment-processing-status-filter-label"
              label="Status"
              value={statusFilter}
              displayEmpty
              renderValue={(selected) =>
                STATUS_FILTER_OPTIONS.find((opt) => opt.value === selected)?.label ??
                'All statuses'
              }
              onChange={(event) =>
                replaceParams({ status: event.target.value, page: 0 })
              }
            >
              {STATUS_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 280 } }}>
            <InputLabel id="payment-processing-shwapno-account-filter-label">
              Shwapno account
            </InputLabel>
            <Select
              labelId="payment-processing-shwapno-account-filter-label"
              label="Shwapno account"
              value={shwapnoAccountFilter}
              onChange={(event) =>
                replaceParams({ shwapno_account: event.target.value, page: 0 })
              }
            >
              <MenuItem value="">All accounts</MenuItem>
              {shwapnoAccountOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            select
            label="Period"
            size="small"
            value={datePreset}
            onChange={(event) => {
              const next = event.target.value
              replaceParams({
                datePreset: next,
                dateFrom: next === 'custom' ? dateFrom : '',
                dateTo: next === 'custom' ? dateTo : '',
                page: 0,
              })
            }}
            sx={{ minWidth: { xs: '100%', md: 170 } }}
          >
            {DATE_PRESETS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {datePreset === 'custom' && (
            <>
              <TextField
                label="From"
                type="date"
                size="small"
                value={dateFrom}
                onChange={(event) =>
                  replaceParams({ dateFrom: event.target.value, page: 0 })
                }
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: '100%', md: 160 } }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={dateTo}
                onChange={(event) =>
                  replaceParams({ dateTo: event.target.value, page: 0 })
                }
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: '100%', md: 160 } }}
              />
            </>
          )}
        </Stack>
        {datePreset === 'custom' && !customReady && (
          <Typography variant="caption" color="text.secondary">
            Choose a start and end date. The start must be on or before the end.
          </Typography>
        )}
        </Stack>
      </Card>

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Total records: {total}
          </Typography>
          {showDownloadExcel ? (
            <Button
              variant="outlined"
              size="small"
              disabled={exportDisabled}
              onClick={handleExportExcel}
              startIcon={
                exporting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FileDownloadIcon />
                )
              }
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                borderColor: '#e90000',
                color: '#e90000',
                '&:hover': { borderColor: '#c40000', bgcolor: 'rgba(233,0,0,0.04)' },
              }}
            >
              {exporting ? 'Exporting…' : 'Download Excel'}
            </Button>
          ) : null}
        </Box>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size={isCompact ? 'small' : 'medium'} sx={{ minWidth: 1780 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                <TableCell sx={headerCellSx}>Shop Name</TableCell>
                <TableCell sx={headerCellSx}>Shop ID</TableCell>
                <TableCell sx={headerCellSx}>Amount</TableCell>
                <TableCell sx={headerCellSx}>Deposit Slip</TableCell>
                <TableCell sx={headerCellSx}>Deposited Bank (Shwapno)</TableCell>
                <TableCell sx={headerCellSx}>Shwapno Account / Mobile No</TableCell>
                <TableCell sx={headerCellSx}>Transaction Time &amp; Date</TableCell>
                <TableCell sx={headerCellSx}>Upload Time &amp; Date</TableCell>
                <TableCell sx={headerCellSx}>Mode of Deposit</TableCell>
                <TableCell sx={headerCellSx}>Transaction / Chq. No</TableCell>
                <TableCell sx={headerCellSx}>Depositor A/C No</TableCell>
                {canVerifyPaymentProof ? (
                  <TableCell sx={headerCellSx}>Actions</TableCell>
                ) : null}
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Verification Time &amp; Date</TableCell>
                <TableCell sx={headerCellSx} align="center">View</TableCell>
                <TableCell sx={headerCellSx}>Deposit ID</TableCell>
                <TableCell sx={headerCellSx}>Proof</TableCell>
                <TableCell sx={headerCellSx}>Order ID</TableCell>
                <TableCell sx={headerCellSx}>Uploaded By</TableCell>
                <TableCell sx={headerCellSx}>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : rows.length === 0 ? (
                <TableEmptyState
                  colSpan={tableColSpan}
                  message={
                    statusFilter === 'rejected'
                      ? 'No rejected payment proofs in this view.'
                      : statusFilter
                        ? 'No payments awaiting verification.'
                        : 'No payment proofs in this view.'
                  }
                />
              ) : (
                rows.map((row) => {
                  const slipUrl = row.deposit_slip_url
                  const slipKey = `${row.request_id}-${row.proof_id}`
                  const statusUpper = String(row.status || '').toUpperCase()
                  const requestStatusUpper = String(
                    row.request_status || '',
                  ).toUpperCase()
                  const isVerified = statusUpper === 'VERIFIED'
                  const proofPending = statusUpper === 'PENDING'
                  const requestPending = requestStatusUpper === 'PENDING'
                  const showRowActions =
                    canVerifyPaymentProof && proofPending && requestPending
                  const proofLabel =
                    row.proof_count > 1
                      ? `${row.proof_index} of ${row.proof_count}`
                      : null
                  return (
                    <TableRow key={slipKey} hover>
                      <TableCell>{cellDash(row.shop_name)}</TableCell>
                      <TableCell>{cellDash(row.shop_id)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatOrderAmount(row.amount)}
                      </TableCell>
                      <TableCell>
                        {slipUrl ? (
                          isLikelyImageUrl(slipUrl) ? (
                            <Link
                              component="button"
                              type="button"
                              variant="body2"
                              onClick={() => setPreviewUrl(slipUrl)}
                            >
                              View slip
                            </Link>
                          ) : (
                            <Link href={slipUrl} target="_blank" rel="noopener noreferrer">
                              Open attachment
                            </Link>
                          )
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{cellDash(row.deposited_bank_shwapno)}</TableCell>
                      <TableCell>{cellDash(row.shwapno_account_or_mobile)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(row.transaction_at)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(row.upload_at)}
                      </TableCell>
                      <TableCell>{cellDash(row.mode_of_deposit)}</TableCell>
                      <TableCell>
                        {cellDash(row.transaction_or_cheque_id)}
                        {row.cheque_date ? (
                          <Typography
                            variant="caption"
                            component="div"
                            color="text.secondary"
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Chq. {formatDate(row.cheque_date)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{cellDash(row.depositor_account_no)}</TableCell>
                      {canVerifyPaymentProof ? (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {showRowActions ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleVerifyRow(row)}
                                disabled={
                                  paymentProofRequestActionId ===
                                  String(row.request_id)
                                }
                                sx={{
                                  bgcolor: '#11809A',
                                  '&:hover': { bgcolor: '#0f6d82' },
                                  textTransform: 'none',
                                }}
                              >
                                Verify
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                  setPaymentProofRejectTarget({
                                    id: row.request_id,
                                    proofId: row.proof_id,
                                    order_id: row.order_id,
                                  })
                                  setPaymentProofRejectReason('')
                                  setPaymentProofRejectError(null)
                                }}
                                disabled={paymentProofRejectSubmitting}
                                sx={{ textTransform: 'none' }}
                              >
                                Reject
                              </Button>
                            </Stack>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      ) : null}
                      <TableCell>{paymentProofStatusCell(row)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {isVerified
                          ? formatDateTime(row.verified_at)
                          : cellDash(null)}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          size="small"
                          title="View deposit details"
                          aria-label="View deposit details"
                          onClick={() => setPaymentProofPreviewTarget(row)}
                          sx={{ color: '#e90000' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {row.deposit_id && row.request_id ? (
                          <Link
                            component={RouterLink}
                            to={`/orders/purchase/deposits/${row.request_id}`}
                            fontWeight={600}
                            color="#e90000"
                            underline="hover"
                          >
                            {row.deposit_id}
                          </Link>
                        ) : (
                          cellDash(row.deposit_id)
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {cellDash(proofLabel)}
                      </TableCell>
                      <TableCell>
                        {row.order_id ? (
                          <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={() => handleOrderClick(row.order_id)}
                            sx={{ fontWeight: 600 }}
                          >
                            {row.order_id}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {cellDash(row.uploaded_by)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>{cellDash(row.remarks)}</TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_event, newPage) => replaceParams({ page: newPage })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            const next = parseInt(event.target.value, 10)
            replaceParams({ limit: next, page: 0 })
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage={isCompact ? 'Rows' : 'Rows per page'}
          sx={paginationSx}
        />
      </Card>

      <Dialog
        open={Boolean(slipGallery)}
        onClose={() => setSlipGallery(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {slipGallery?.depositId
              ? `Deposit slips — ${slipGallery.depositId}`
              : 'Deposit slips'}
          </Typography>
          <IconButton onClick={() => setSlipGallery(null)} sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {(slipGallery?.proofs || []).map((proof, proofIndex) => {
              const url = proof.file_url
              const labelParts = [
                proof.mode_of_deposit,
                proof.requested_amount != null
                  ? formatOrderAmount(proof.requested_amount)
                  : null,
              ].filter(Boolean)
              const label =
                labelParts.length > 0
                  ? labelParts.join(' · ')
                  : `Attachment ${proofIndex + 1}`
              if (!url) {
                return (
                  <Typography key={proof.proof_id ?? proofIndex} variant="body2" color="text.secondary">
                    {label} — no file
                  </Typography>
                )
              }
              return (
                <Stack
                  key={proof.proof_id ?? `${url}-${proofIndex}`}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ flexWrap: 'wrap' }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {isLikelyImageUrl(url) ? (
                      <Button size="small" onClick={() => setPreviewUrl(url)}>
                        Preview
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open attachment
                    </Button>
                  </Stack>
                </Stack>
              )
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlipGallery(null)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(previewUrl)}
        onClose={() => setPreviewUrl(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh', m: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Deposit slip
          </Typography>
          <IconButton onClick={() => setPreviewUrl(null)} sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#000',
          }}
        >
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Deposit slip"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button onClick={() => setPreviewUrl(null)} color="inherit">
            Close
          </Button>
          {previewUrl && (
            <Button
              variant="contained"
              component="a"
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AdminPaymentProofPreviewDialog
        open={Boolean(paymentProofPreviewTarget)}
        row={paymentProofPreviewTarget}
        onClose={() => setPaymentProofPreviewTarget(null)}
        canVerify={canVerifyPaymentProof}
        onUpdated={() => setListRefreshToken((t) => t + 1)}
        onVerifyProof={handleVerifyProofInPreview}
        onRejectProof={handleRejectProofInPreview}
        proofActionSubmitting={paymentProofProofActionSubmitting}
      />

      <PaymentProofVerifyDialog
        open={Boolean(paymentProofVerifyTarget)}
        request={paymentProofVerifyTarget}
        onClose={() => {
          if (paymentProofRequestActionId) return
          setPaymentProofVerifyTarget(null)
        }}
        onConfirm={handleConfirmVerifyPaymentProofRequest}
        submitting={
          paymentProofVerifyTarget != null &&
          paymentProofRequestActionId === String(paymentProofVerifyTarget.id)
        }
      />

      <Dialog
        open={Boolean(paymentProofRejectTarget)}
        onClose={closePaymentProofRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
          Reject payment proof request
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {paymentProofRejectError ? (
            <Alert
              severity="error"
              onClose={() => setPaymentProofRejectError(null)}
              sx={{ mb: 2 }}
            >
              {paymentProofRejectError}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Reject request{' '}
            <strong>#{paymentProofRejectTarget?.id ?? '—'}</strong>? The shop or an admin can
            edit and resubmit the payment proof from the rejected queue or order detail.
          </Typography>
          <TextField
            autoFocus
            label="Rejection reason"
            value={paymentProofRejectReason}
            onChange={(e) => {
              const value = e.target.value
              if (value.length <= PAYMENT_PROOF_REJECT_REASON_MAX) {
                setPaymentProofRejectReason(value)
                setPaymentProofRejectError(null)
              }
            }}
            fullWidth
            multiline
            rows={4}
            required
            disabled={paymentProofRejectSubmitting}
            placeholder="Enter reason for rejection…"
            helperText={`${paymentProofRejectReason.length}/${PAYMENT_PROOF_REJECT_REASON_MAX} characters`}
            inputProps={{ maxLength: PAYMENT_PROOF_REJECT_REASON_MAX }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button
            onClick={closePaymentProofRejectDialog}
            disabled={paymentProofRejectSubmitting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRejectPaymentProofRequest}
            variant="contained"
            color="error"
            disabled={
              paymentProofRejectSubmitting || !paymentProofRejectReason.trim()
            }
            startIcon={
              paymentProofRejectSubmitting ? <CircularProgress size={16} /> : undefined
            }
          >
            {paymentProofRejectSubmitting ? 'Rejecting…' : 'Reject request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PaymentProcessingListPage
